import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'
import { Grid } from '@mui/material'

import Button from './Button'
import Dialog from './Dialog'
import Select from './Select'
import InputField from './InputField'
import AutoComplete from './AutoComplete'
import GridCheckbox from './GridCheckbox'
import GridCircularProgress from './GridCircularProgress'
import { useFeedback } from '../hooks/feedback-hook'
import { sortAlphabetically, sortGironi } from '../hooks/sort-functions'
import {
  useCreateCalendarioMutation,
  useGetCampionatiQuery,
  useGetCategorieQuery,
  useGetIscrizioniQuery,
  useGetTabelloniQuery,
} from '../hooks/api-slice'
import { groupByGirone, formatTabelloneName } from '../hooks/utils-functions'
import { setSelectedCampionato } from '../container/selection'
import { titlesImpostazioni } from '../settings/settings'

const DialogTabellone = ({ open, handleClose }) => {
  const { data: campionati } = useGetCampionatiQuery()
  const { data: categorie } = useGetCategorieQuery()

  const [campionato, setCampionato] = useState()
  const [categoria, setCategoria] = useState()

  const { data: squadre } = useGetIscrizioniQuery(
    { campionato, categoria },
    { skip: !(campionato && categoria) },
  )

  const squadreN = [...(squadre || [])]?.sort(
    (a, b) =>
      sortGironi(a.girone, b.girone) ||
      sortAlphabetically(a.nomeSquadra, b.nomeSquadra),
  )

  const { data: tabelloniAll } = useGetTabelloniQuery()
  const tabelloni = tabelloniAll?.filter(
    n => squadre && n.squadre <= squadre.length,
  )

  const [createCalendarioMutation] = useCreateCalendarioMutation()

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { setFeedback } = useFeedback()

  const campionatiAperti = campionati?.filter(c => !c.campionatoChiuso)

  const [prefisso, setPrefisso] = useState()
  const [tabellone, setTabellone] = useState()
  const [sfide, setSfide] = useState()
  const [fasi, setFasi] = useState()
  const [soloAndata, setSoloAndata] = useState()
  const [arbitraggio, setArbitraggio] = useState()
  const [squadreScelte, setSquadreScelte] = useState()
  const [isLoading, setIsLoading] = useState()

  // update sfide
  useEffect(() => {
    setSfide(tabelloni?.find(t => t._id === tabellone)?.sfide)
  }, [tabellone, tabelloni])

  // update fasi
  useEffect(() => {
    setFasi(
      sfide
        ?.map(s => +s.fase)
        ?.filter((v, i, a) => a.indexOf(v) === i)
        ?.sort((a, b) => a - b),
    )
  }, [sfide])

  // update soloAndata and arbitraggio
  useEffect(() => {
    setSoloAndata(Array(fasi?.length).fill(false))
    setArbitraggio(Array(fasi?.length).fill(false))
  }, [fasi])

  // set random squadreScelte
  const squadreRandom = () => {
    const randomTeams = squadre?.slice()?.sort(() => Math.random() - 0.5)

    setSquadreScelte(
      sfide
        ?.filter(s => +s.fase === 1)
        ?.map((s, i) => [
          { sfida: +s.sfida, n: 1, team: randomTeams?.[i * 2] },
          { sfida: +s.sfida, n: 2, team: randomTeams?.[i * 2 + 1] },
        ])
        ?.flat(),
    )
  }

  useEffect(() => {
    setTabellone()
    setPrefisso()
    setSquadreScelte()
  }, [campionato, categoria])

  useEffect(() => {
    setCampionato()
    setCategoria()
    setTabellone()
    setPrefisso()
    setSoloAndata()
    setArbitraggio()
    setSquadreScelte()
  }, [open])

  const setPlaceholders = (
    numeriGaraPrecV,
    numeriGaraPrecP,
    fasePrecSoloAndata,
  ) => {
    let placeholderCasa, placeholderTrasferta

    if (numeriGaraPrecV?.length === 2 && numeriGaraPrecP?.length === 0) {
      placeholderCasa = !fasePrecSoloAndata
        ? `Vincente gare ${numeriGaraPrecV[0]}`
        : `Vincente gara ${numeriGaraPrecV[0]}`
      placeholderTrasferta = !fasePrecSoloAndata
        ? `Vincente gare ${numeriGaraPrecV[1]}`
        : `Vincente gara ${numeriGaraPrecV[1]}`
    }

    if (numeriGaraPrecV?.length === 0 && numeriGaraPrecP?.length === 2) {
      placeholderCasa = !fasePrecSoloAndata
        ? `Perdente gare ${numeriGaraPrecP[0]}`
        : `Perdente gara ${numeriGaraPrecP[0]}`
      placeholderTrasferta = !fasePrecSoloAndata
        ? `Perdente gare ${numeriGaraPrecP[1]}`
        : `Perdente gara ${numeriGaraPrecP[1]}`
    }

    if (numeriGaraPrecV?.length === 1 && numeriGaraPrecP?.length === 0) {
      placeholderCasa = !fasePrecSoloAndata
        ? `Vincente gare ${numeriGaraPrecV[0]}`
        : `Vincente gara ${numeriGaraPrecV[0]}`
      placeholderTrasferta = 'Ripescaggio'
    }

    if (numeriGaraPrecV?.length === 0 && numeriGaraPrecP?.length === 1) {
      placeholderCasa = !fasePrecSoloAndata
        ? `Perdente gare ${numeriGaraPrecP[0]}`
        : `Perdente gara ${numeriGaraPrecP[0]}`
      placeholderTrasferta = 'Ripescaggio'
    }

    return [placeholderCasa, placeholderTrasferta]
  }

  const createCalendario = async body => {
    const result = await createCalendarioMutation(body)
    if (result?.error) throw new Error(result?.error?.data?.message)
  }

  const createTabellone = async () => {
    if (!sfide?.length) throw new Error('Sfide non trovate!')

    if (!fasi?.length) throw new Error('Fasi non trovate!')

    if (
      sfide?.filter(s => +s.fase === 1)?.length * 2 !==
      squadreScelte?.length
    ) {
      const nSquadre = squadreScelte?.length || 0
      const nSfide = sfide?.filter(s => +s.fase === 1)?.length || 0
      throw new Error(
        `Sono state scelte ${nSquadre} squadre per ${nSfide} sfide!`,
      )
    }

    if (!prefisso) throw new Error('Prefisso numero gara non trovato!')

    const fNumeroGara = n => `${prefisso}${n.toString().padStart(2, '0')}`
    const nomeSquadra = (nSfida, nIndice) =>
      squadreScelte?.find(s => s.sfida === nSfida && s.n === nIndice)?.team.id

    const sfideNumeriGara = sfide
      ?.map(x =>
        soloAndata?.[+x.fase - 1]
          ? [{ sfida: +x.sfida }]
          : [{ sfida: +x.sfida }, { sfida: +x.sfida }],
      )
      ?.flat()
      ?.sort((a, b) => a.sfida - b.sfida)
      ?.map((x, i) => {
        return { ...x, numeroGara: fNumeroGara(i + 1) }
      })

    for (const s of sfide) {
      const fase = +s.fase
      const faseSoloAndata = soloAndata?.[fase - 1]
      const fasePrecSoloAndata = soloAndata?.[fase - 2]
      const faseArbitraggio = arbitraggio?.[fase - 1]

      const sfida = +s.sfida
      const numeriGara = sfideNumeriGara
        ?.filter(x => x.sfida === sfida)
        ?.map(x => x.numeroGara)
        ?.sort()

      // placeholder per fasi successive
      const numeriGaraPrecV = sfide
        ?.filter(x => +x.sfidaVincente === sfida)
        ?.map(x =>
          sfideNumeriGara
            ?.filter(y => +y.sfida === +x.sfida)
            ?.map(y => y.numeroGara)
            ?.join('-'),
        )
      const numeriGaraPrecP = sfide
        ?.filter(x => +x.sfidaPerdente === sfida)
        ?.map(x =>
          sfideNumeriGara
            ?.filter(y => +y.sfida === +x.sfida)
            ?.map(y => y.numeroGara)
            ?.join('-'),
        )
      const [placeholderCasa, placeholderTrasferta] = setPlaceholders(
        numeriGaraPrecV,
        numeriGaraPrecP,
        fasePrecSoloAndata,
      )

      let body = {
        codiceCampionato: campionato,
        categoria: categoria,
        girone: 'finali',
        giornata: fase,
        needsReferee: faseArbitraggio,
        sfidaTabellone: sfida,
      }

      if (s.sfidaVincente) body = { ...body, sfidaVincente: s.sfidaVincente }
      if (s.sfidaPerdente) body = { ...body, sfidaPerdente: s.sfidaPerdente }

      // set campo neutro
      if (faseSoloAndata && fase === fasi[fasi.length - 1])
        body = { ...body, indirizzo: 'Campo neutro', citta: 'Da definire' }

      if (faseSoloAndata) {
        // solo andata
        await createCalendario({
          ...body,
          numeroGara: numeriGara?.[0],
          squadraCasaID: fase === 1 ? nomeSquadra(sfida, 1) : placeholderCasa,
          squadraTrasfertaID:
            fase === 1 ? nomeSquadra(sfida, 2) : placeholderTrasferta,
        })
      } else {
        // andata
        await createCalendario({
          ...body,
          numeroGara: numeriGara?.[1], // Ferruccio convention
          squadraCasaID:
            fase === 1 ? nomeSquadra(sfida, 2) : placeholderTrasferta,
          squadraTrasfertaID:
            fase === 1 ? nomeSquadra(sfida, 1) : placeholderCasa,
        })

        // ritorno
        await createCalendario({
          ...body,
          numeroGara: numeriGara?.[0], // Ferruccio convention
          squadraCasaID: fase === 1 ? nomeSquadra(sfida, 1) : placeholderCasa,
          squadraTrasfertaID:
            fase === 1 ? nomeSquadra(sfida, 2) : placeholderTrasferta,
        })
      }
    }

    setFeedback('Tabellone generato correttamente!', false)
    dispatch(
      setSelectedCampionato({ selectedCampionato: campionato, campionati }),
    )
    navigate('/finali')
  }

  return (
    <Dialog
      open={open}
      handleClose={handleClose}
      title={titlesImpostazioni.titleTabellone}
      maxWidth='lg'
      stretchHeight
    >
      <Grid container spacing={3} sx={{ width: '100%' }}>
        <Grid
          container
          paddingTop={1}
          alignItems='center'
          sx={{ width: '100%' }}
        >
          <Grid size={{ xs: 12, md: 3 }}>
            <Select
              id='campionato'
              value={campionato}
              options={campionatiAperti?.map(a => a.codice)}
              optionLabels={campionatiAperti?.map(a => a.nome)}
              label='Campionato'
              disabled={!campionatiAperti}
              onChange={setCampionato}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Select
              id='categoria'
              value={categoria}
              options={categorie?.map(a => a.categoria)}
              optionLabels={categorie?.map(a => a.nome)}
              label='Categoria'
              disabled={!categorie}
              onChange={setCategoria}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <InputField
              id='prefisso'
              value={prefisso}
              onChange={setPrefisso}
              label='Prefisso numero gara'
              disabled={!campionato || !categoria}
              errorFunc={x => !x}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Select
              id='tabellone'
              value={tabellone}
              options={tabelloni?.map(t => t._id)}
              optionLabels={tabelloni?.map(formatTabelloneName)}
              label='Tabellone'
              disabled={!squadre || !tabelloni}
              onChange={setTabellone}
            />
          </Grid>
        </Grid>
        {fasi && soloAndata && arbitraggio && (
          <Grid container spacing={0} sx={{ width: '100%' }}>
            <Grid
              container
              alignItems='center'
              justifyContent='center'
              sx={{ width: '100%' }}
            >
              <Grid size={{ xs: 4, md: 3 }} />
              <Grid size={{ xs: 4, md: 3 }}>
                <center>Solo andata?</center>
              </Grid>
              <Grid size={{ xs: 4, md: 3 }}>
                <center>Arbitraggio ufficiale?</center>
              </Grid>
            </Grid>
            <Grid container sx={{ width: '100%' }}>
              {fasi?.map(n => {
                return (
                  <Grid
                    key={n}
                    container
                    alignItems='center'
                    justifyContent='center'
                    sx={{ width: '100%' }}
                  >
                    <Grid size={{ xs: 4, md: 3 }}>
                      <center>
                        {n === fasi?.length
                          ? 'Finali'
                          : n === fasi?.length - 1
                            ? 'Semifinali'
                            : `Fase ${n}`}
                      </center>
                    </Grid>
                    <Grid size={{ xs: 4, md: 3 }}>
                      <GridCheckbox
                        value={soloAndata[n - 1] || false}
                        onChange={(_, y) =>
                          setSoloAndata(
                            soloAndata.map((x, i) => (i === n - 1 ? y : x)),
                          )
                        }
                        md={12}
                      />
                    </Grid>
                    <Grid size={{ xs: 4, md: 3 }}>
                      <GridCheckbox
                        value={arbitraggio[n - 1] || false}
                        onChange={(_, y) =>
                          setArbitraggio(
                            arbitraggio.map((x, i) => (i === n - 1 ? y : x)),
                          )
                        }
                        md={12}
                      />
                    </Grid>
                  </Grid>
                )
              })}
            </Grid>
          </Grid>
        )}
        {sfide && (
          <>
            <Grid
              container
              spacing={2}
              alignItems='center'
              sx={{ width: '100%' }}
            >
              <Grid size={{ xs: 12, md: 3 }}>
                <center>
                  <Button error onClick={() => setSquadreScelte()}>
                    Reset squadre
                  </Button>
                </center>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <center>
                  <b>Incontri della prima fase</b>
                </center>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <center>
                  <Button dark onClick={squadreRandom}>
                    Squadre a caso
                  </Button>
                </center>
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ width: '100%' }}>
              {sfide
                ?.filter(s => +s.fase === 1)
                ?.map(s => +s.sfida)
                ?.map((sfida, i) => {
                  const i1 = (i + 1) * 2 - 1
                  const i2 = (i + 1) * 2
                  return (
                    <Grid
                      key={i}
                      container
                      alignItems='center'
                      sx={{ width: '100%' }}
                    >
                      <Grid size={{ xs: 12, md: 5 }}>
                        <center>
                          <AutoComplete
                            id={`squadra-${i1}`}
                            value={
                              squadreScelte?.find(
                                s => s.sfida === sfida && s.n === 1,
                              )?.team
                            }
                            label={`Squadra ${i1}`}
                            options={squadreN}
                            groupBy={groupByGirone}
                            getOptionLabel={op => op.nomeSquadra}
                            getOptionDisabled={op =>
                              squadreScelte
                                ?.map(s => s.team?.id)
                                ?.includes(op.id)
                            }
                            onChange={value => {
                              const newChoice = { sfida, n: 1, team: value }
                              setSquadreScelte(
                                squadreScelte
                                  ? [
                                      ...squadreScelte?.filter(
                                        s => s.sfida !== sfida || s.n !== 1,
                                      ),
                                      newChoice,
                                    ]?.sort((a, b) => a.n - b.n)
                                  : [newChoice],
                              )
                            }}
                          />
                        </center>
                      </Grid>
                      <Grid size={{ xs: 12, md: 2 }}>
                        <center>contro</center>
                      </Grid>
                      <Grid size={{ xs: 12, md: 5 }}>
                        <center>
                          <AutoComplete
                            id={`squadra-${i2}`}
                            value={
                              squadreScelte?.find(
                                s => s.sfida === sfida && s.n === 2,
                              )?.team
                            }
                            label={`Squadra ${i2}`}
                            options={squadreN}
                            groupBy={groupByGirone}
                            getOptionLabel={op => op.nomeSquadra}
                            getOptionDisabled={op =>
                              squadreScelte
                                ?.map(s => s.team?.id)
                                ?.includes(op.id)
                            }
                            onChange={value => {
                              const newChoice = { sfida, n: 2, team: value }
                              setSquadreScelte(
                                squadreScelte
                                  ? [
                                      ...squadreScelte?.filter(
                                        s => s.sfida !== sfida || s.n !== 2,
                                      ),
                                      newChoice,
                                    ]?.sort((a, b) => a.n - b.n)
                                  : [newChoice],
                              )
                            }}
                          />
                        </center>
                      </Grid>
                    </Grid>
                  )
                })}
            </Grid>
          </>
        )}
        <Grid size={12}>
          Cliccando su <b>GENERA TABELLONE</b> verranno create tutte le partite
          del tabellone a eliminazione e verranno impostate le fasi successive
          fino alle finali 1°/2° e 3°/4° posto.
        </Grid>
        <Grid size={12}>
          <center>
            <Button
              excel
              disabled={
                isLoading ||
                !campionato ||
                !categoria ||
                !tabellone ||
                !prefisso ||
                squadreScelte?.length !==
                  sfide?.filter(s => +s.fase === 1)?.length * 2
              }
              onClick={async () => {
                setIsLoading(true)
                try {
                  await createTabellone()
                } catch (err) {
                  setFeedback(err.message)
                }
                setIsLoading()
              }}
            >
              {(isLoading && (
                <GridCircularProgress color='inherit' size='1rem' />
              )) ||
                'Genera tabellone'}
            </Button>
          </center>
        </Grid>
      </Grid>
    </Dialog>
  )
}

export default DialogTabellone
